import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Buildings, ShieldCheck, Info, EnvelopeSimple, UserPlus, Copy, Trash, Warning, SignOut, DownloadSimple,
} from "@phosphor-icons/react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const selCls = "mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500";

const RATES = [
  ["EPF (KWSP)", "Employee 11%", "Employer 13% (≤RM5,000) / 12% (>RM5,000)"],
  ["SOCSO (PERKESO)", "Employee 0.5%", "Employer 1.75% · ceiling RM6,000"],
  ["EIS (SIP)", "Employee 0.2%", "Employer 0.2% · ceiling RM6,000"],
  ["PCB (MTD)", "Progressive", "Annualised tax with standard reliefs"],
];

const Card = ({ icon: Icon, tone, title, children, className = "" }) => (
  <div className={`border border-[#E2E5EA] bg-[#FFFFFF] p-6 ${className}`}>
    <div className={`flex items-center gap-2 ${tone}`}>
      <Icon size={20} weight="bold" />
      <h3 className="font-heading text-lg font-semibold text-[#0F172A]">{title}</h3>
    </div>
    <div className="mt-5">{children}</div>
  </div>
);

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [company, setCompany] = useState({ company_name: "", company_address: "", company_reg_no: "", payslip_accent: "#EF4444", logo: "" });
  const [otMult, setOtMult] = useState({ ot_mult_normal: 1.5, ot_mult_rest: 2.0, ot_mult_holiday: 3.0 });
  const [sched, setSched] = useState({ working_days_per_month: 26, working_hours_per_day: 8, working_mode: "normal", shift_type: "2", shift_hours_2: 12, shift_hours_3: 8 });
  const hoursPreset = [8, 7.5].includes(parseFloat(sched.working_hours_per_day)) ? String(sched.working_hours_per_day) : "custom";
  const [savingCompany, setSavingCompany] = useState(false);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const logoRef = useRef(null);

  const loadInvites = () => api.get("/invites").then(({ data }) => setInvites(data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setEmailEnabled(data.email_enabled);
      setCompany({ company_name: data.company_name || "", company_address: data.company_address || "",
                   company_reg_no: data.company_reg_no || "", payslip_accent: data.payslip_accent || "#EF4444", logo: data.logo || "" });
      setOtMult({ ot_mult_normal: data.ot_mult_normal ?? 1.5, ot_mult_rest: data.ot_mult_rest ?? 2.0, ot_mult_holiday: data.ot_mult_holiday ?? 3.0 });
      setSched({ working_days_per_month: data.working_days_per_month ?? 26, working_hours_per_day: data.working_hours_per_day ?? 8,
                 working_mode: data.working_mode || "normal", shift_type: data.shift_type || "2",
                 shift_hours_2: data.shift_hours_2 ?? 12, shift_hours_3: data.shift_hours_3 ?? 8 });
    }).catch(() => {});
    if (isAdmin) loadInvites();
  }, [isAdmin]);

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put("/settings", {
        company_name: company.company_name,
        company_address: company.company_address,
        company_reg_no: company.company_reg_no,
        payslip_accent: company.payslip_accent,
        ot_mult_normal: parseFloat(otMult.ot_mult_normal) || 1.5,
        ot_mult_rest: parseFloat(otMult.ot_mult_rest) || 2.0,
        ot_mult_holiday: parseFloat(otMult.ot_mult_holiday) || 3.0,
        working_days_per_month: parseFloat(sched.working_days_per_month) || 26,
        working_hours_per_day: parseFloat(sched.working_hours_per_day) || 8,
        working_mode: sched.working_mode,
        shift_type: sched.shift_type,
        shift_hours_2: parseFloat(sched.shift_hours_2) || 12,
        shift_hours_3: parseFloat(sched.shift_hours_3) || 8,
      });
      toast.success("Company information saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingCompany(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo: data.logo }));
      toast.success("Logo updated — it will appear on payslips");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete("/settings/logo");
      setCompany((c) => ({ ...c, logo: data.logo }));
      toast.success("Logo removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const download = (path, filename) => {
    const token = localStorage.getItem("pf_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error(); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Download failed"));
  };

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleEmail = async (val) => {
    setEmailEnabled(val);
    try {
      await api.put("/settings", { email_enabled: val });
      toast.success(val ? "Outbound email enabled" : "Outbound email disabled");
    } catch (err) {
      setEmailEnabled(!val);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendInvite = async () => {
    if (!inviteEmail) { toast.error("Enter an email address"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/invites", { email: inviteEmail });
      if (data.emailed) toast.success(`Invitation emailed to ${data.email}`);
      else toast.success("Invite created — copy the link to share");
      setInviteEmail("");
      loadInvites();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setInviting(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard?.writeText(link);
    toast.success("Invite link copied");
  };

  const revoke = async (id) => {
    try { await api.delete(`/invites/${id}`); toast.success("Invite revoked"); loadInvites(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout title="Settings" subtitle="Company profile, team access and statutory configuration">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card icon={Buildings} tone="text-red-500" title="Company Information">
          {isAdmin ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#E2E5EA] bg-[#F4F5F7]">
                  {company.logo ? (
                    <img src={company.logo} alt="Company logo" className="h-full w-full object-contain" data-testid="company-logo-preview" />
                  ) : (
                    <Buildings size={24} className="text-[#9CA3AF]" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-[#6B7280]">Company Logo (shows on payslips)</p>
                  <div className="mt-1.5 flex gap-2">
                    <Button size="sm" variant="outline" data-testid="upload-logo-btn" onClick={() => logoRef.current?.click()} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#F4F5F7] hover:text-red-500">
                      Upload
                    </Button>
                    {company.logo && (
                      <Button size="sm" variant="outline" data-testid="remove-logo-btn" onClick={removeLogo} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#F4F5F7] hover:text-red-500">
                        Remove
                      </Button>
                    )}
                    <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/*" className="hidden" data-testid="logo-file-input" onChange={uploadLogo} />
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6B7280]">Company Name</label>
                <Input data-testid="company-name-input" value={company.company_name} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6B7280]">Address</label>
                <Input data-testid="company-address-input" value={company.company_address} onChange={(e) => setCompany((c) => ({ ...c, company_address: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6B7280]">Registration No</label>
                <Input data-testid="company-reg-input" value={company.company_reg_no} onChange={(e) => setCompany((c) => ({ ...c, company_reg_no: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6B7280]">Payslip Accent Colour</label>
                <div className="mt-1.5 flex items-center gap-3">
                  <input type="color" data-testid="accent-color-input" value={company.payslip_accent} onChange={(e) => setCompany((c) => ({ ...c, payslip_accent: e.target.value }))} className="h-9 w-14 cursor-pointer rounded border border-[#E2E5EA] bg-white" />
                  <Input data-testid="accent-hex-input" value={company.payslip_accent} onChange={(e) => setCompany((c) => ({ ...c, payslip_accent: e.target.value }))} className="w-32 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
                  <span className="text-xs text-[#9CA3AF]">Used on payslips &amp; EA forms</span>
                </div>
              </div>
              <div className="border-t border-[#EDEFF2] pt-4">
                <p className="mb-1 text-xs uppercase tracking-wider text-[#6B7280]">Working Hours &amp; Mode</p>
                <p className="mb-3 text-xs text-[#9CA3AF]">Company defaults for the OT hourly rate. Each employee can override these in their profile.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-[#6B7280]">Working days / month</label>
                    <Input data-testid="working-days-input" type="number" step="1" value={sched.working_days_per_month} onChange={(e) => setSched((s) => ({ ...s, working_days_per_month: e.target.value }))} className={selCls} />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280]">Working hours / day</label>
                    <div className="mt-1.5 flex gap-2">
                      <select data-testid="working-hours-select" value={hoursPreset} onChange={(e) => setSched((s) => ({ ...s, working_hours_per_day: e.target.value === "custom" ? "" : e.target.value }))} className="h-9 flex-1 rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-2 text-sm text-[#0F172A]">
                        <option value="8">8.0 hours (default)</option>
                        <option value="7.5">7.5 hours</option>
                        <option value="custom">Custom</option>
                      </select>
                      {hoursPreset === "custom" && (
                        <Input data-testid="working-hours-custom" type="number" step="0.5" placeholder="hrs" value={sched.working_hours_per_day} onChange={(e) => setSched((s) => ({ ...s, working_hours_per_day: e.target.value }))} className="w-24 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280]">Working mode</label>
                    <select data-testid="working-mode-select" value={sched.working_mode} onChange={(e) => setSched((s) => ({ ...s, working_mode: e.target.value }))} className="mt-1.5 h-9 w-full rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-2 text-sm text-[#0F172A]">
                      <option value="normal">Normal (office hours)</option>
                      <option value="shift">Shift</option>
                    </select>
                  </div>
                  {sched.working_mode === "shift" && (
                    <div>
                      <label className="text-xs text-[#6B7280]">Default shift type</label>
                      <select data-testid="shift-type-select" value={sched.shift_type} onChange={(e) => setSched((s) => ({ ...s, shift_type: e.target.value }))} className="mt-1.5 h-9 w-full rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-2 text-sm text-[#0F172A]">
                        <option value="2">2-shift rotation</option>
                        <option value="3">3-shift rotation</option>
                      </select>
                    </div>
                  )}
                  <div>
                    <label className="text-xs text-[#6B7280]">Hours per shift (2-shift)</label>
                    <Input data-testid="shift-hours-2" type="number" step="0.5" value={sched.shift_hours_2} onChange={(e) => setSched((s) => ({ ...s, shift_hours_2: e.target.value }))} className={selCls} />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280]">Hours per shift (3-shift)</label>
                    <Input data-testid="shift-hours-3" type="number" step="0.5" value={sched.shift_hours_3} onChange={(e) => setSched((s) => ({ ...s, shift_hours_3: e.target.value }))} className={selCls} />
                  </div>
                </div>
              </div>
              <div className="border-t border-[#EDEFF2] pt-4">
                <p className="mb-1 text-xs uppercase tracking-wider text-[#6B7280]">Overtime Multipliers</p>
                <p className="mb-3 text-xs text-[#9CA3AF]" data-testid="ot-formula-hint">Hourly base = gross ÷ {sched.working_days_per_month || 26} ÷ {sched.working_hours_per_day || 8}h (shift staff use their shift hours). Each OT type is paid at hours × this multiplier.</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-[#6B7280]">Normal day</label>
                    <Input data-testid="ot-mult-normal" type="number" step="0.1" value={otMult.ot_mult_normal} onChange={(e) => setOtMult((m) => ({ ...m, ot_mult_normal: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280]">Rest day</label>
                    <Input data-testid="ot-mult-rest" type="number" step="0.1" value={otMult.ot_mult_rest} onChange={(e) => setOtMult((m) => ({ ...m, ot_mult_rest: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280]">Public holiday</label>
                    <Input data-testid="ot-mult-holiday" type="number" step="0.1" value={otMult.ot_mult_holiday} onChange={(e) => setOtMult((m) => ({ ...m, ot_mult_holiday: e.target.value }))} className="mt-1.5 border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500" />
                  </div>
                </div>
              </div>
              <Button data-testid="save-company-btn" onClick={saveCompany} disabled={savingCompany} className="w-full bg-red-500 font-semibold text-white hover:bg-red-600">
                {savingCompany ? "Saving…" : "Save Company Info"}
              </Button>
              <div className="border-t border-[#EDEFF2] pt-4">
                <p className="mb-2 text-xs uppercase tracking-wider text-[#6B7280]">Employee Payroll Data</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" data-testid="settings-export-csv" onClick={() => download("/employees/export/csv", "employees.csv")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#F4F5F7] hover:text-red-500">
                    <DownloadSimple size={15} className="mr-1.5" /> Download CSV
                  </Button>
                  <Button size="sm" variant="outline" data-testid="settings-export-excel" onClick={() => download("/employees/export", "employees.xlsx")} className="border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-[#F4F5F7] hover:text-emerald-500">
                    <DownloadSimple size={15} className="mr-1.5" /> Download Excel
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 text-sm">
              {[["Company Name", company.company_name], ["Address", company.company_address], ["Registration No", company.company_reg_no || "—"]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-6 border-b border-[#EDEFF2] pb-3">
                  <span className="text-[#6B7280]">{k}</span>
                  <span className="text-right text-[#0F172A]">{v}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card icon={ShieldCheck} tone="text-emerald-400" title="Administrator">
          <div className="space-y-4 text-sm">
            {[
              ["Name", user?.name || "—"],
              ["Email", user?.email || "—"],
              ["Role", user?.role === "approver" ? "Approver" : "HR Administrator"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 border-b border-[#EDEFF2] pb-3">
                <span className="text-[#6B7280]">{k}</span>
                <span className="text-right text-[#0F172A]">{v}</span>
              </div>
            ))}
          </div>
          <Button data-testid="settings-logout-btn" onClick={doLogout} variant="outline" className="mt-4 w-full border-[#E2E5EA] bg-transparent text-[#0F172A] hover:bg-red-50 hover:text-red-600">
            <SignOut size={16} className="mr-1.5" /> Log Out
          </Button>
        </Card>

        {isAdmin && (
          <Card icon={EnvelopeSimple} tone="text-violet-400" title="Outbound Email">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-[#0F172A]">Send payslips &amp; invitations by email</p>
                <p className="mt-1 text-xs text-[#9CA3AF]">
                  Emails payslip links to employees on disburse and invite links to approvers.
                </p>
              </div>
              <Switch data-testid="email-toggle" checked={emailEnabled} onCheckedChange={toggleEmail} className="data-[state=checked]:bg-red-500" />
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-md border border-red-500/20 bg-red-500/5 p-3 text-xs text-red-500/90">
              <Warning size={15} weight="fill" className="mt-0.5 shrink-0" />
              <span>
                Emails currently send from a shared provider address and may land in spam.
                Verify your own sending domain before a real rollout. Invite links always work
                even with email off — just copy and share them.
              </span>
            </div>
          </Card>
        )}

        {isAdmin && (
          <Card icon={UserPlus} tone="text-blue-400" title="Team &amp; Invites">
            <p className="mb-3 text-xs text-[#9CA3AF]">Invite an approver to review and sign off payroll runs.</p>
            <div className="flex items-center gap-2">
              <Input
                data-testid="invite-email-input"
                type="email"
                placeholder="approver@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="border-[#E2E5EA] bg-[#F4F5F7] text-[#0F172A] focus-visible:ring-red-500"
              />
              <Button data-testid="send-invite-btn" onClick={sendInvite} disabled={inviting} className="shrink-0 bg-red-500 font-semibold text-white hover:bg-red-600">
                <UserPlus size={16} className="mr-1.5" /> Invite
              </Button>
            </div>

            <div className="mt-4 space-y-2">
              {invites.length === 0 ? (
                <p className="text-sm text-[#6B7280]">No pending invitations.</p>
              ) : (
                invites.map((inv) => (
                  <div key={inv.id} data-testid={`invite-${inv.email}`} className="flex items-center justify-between gap-2 rounded-md border border-[#E2E5EA] bg-[#F4F5F7] px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm text-[#0F172A]">{inv.email}</p>
                      <p className="text-xs text-[#6B7280]">
                        Approver · {inv.expired ? <span className="text-red-400">expired</span> : "pending"}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button data-testid={`copy-invite-${inv.email}`} onClick={() => copyLink(inv.link)} title="Copy invite link" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-red-500"><Copy size={16} /></button>
                      <button onClick={() => revoke(inv.id)} title="Revoke" className="rounded p-1.5 text-[#9CA3AF] hover:bg-[#E2E5EA] hover:text-red-400"><Trash size={16} /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )}

        <Card icon={Info} tone="text-blue-400" title="Statutory Rates (2026)" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E2E5EA] text-left text-xs uppercase tracking-wider text-[#6B7280]">
                  <th className="py-2 pr-4 font-medium">Contribution</th>
                  <th className="py-2 pr-4 font-medium">Employee</th>
                  <th className="py-2 font-medium">Employer / Basis</th>
                </tr>
              </thead>
              <tbody>
                {RATES.map(([n, emp, empr]) => (
                  <tr key={n} className="border-b border-[#EDEFF2]">
                    <td className="py-3 pr-4 font-medium text-[#0F172A]">{n}</td>
                    <td className="py-3 pr-4 text-[#9CA3AF]">{emp}</td>
                    <td className="py-3 text-[#9CA3AF]">{empr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-[#6B7280]">
            Rates apply to Malaysian / PR employees under 60 (Category 1). PCB is an estimate based on the annualised
            method with individual relief (RM9,000) and EPF relief (capped RM4,000); actual filing may vary with additional reliefs.
          </p>
        </Card>
      </div>
    </Layout>
  );
}
